import * as React from 'react';
import { InjectedTranslateProps } from 'react-i18next';
import { Button } from 'wix-style-react';
import { DataHooks } from '@src/constants/data-hooks';
import { SaveFlowStatus } from '@src/types';
import { MissingLinkPrompt } from './prompts/missing-link-prompt';
import { SuccessPrompt } from './prompts/success-prompt';
import { FailedPrompt } from './prompts/failed-message';
import { Actions } from '@src/context/actions/actions';
import { KBLinks } from '@src/constants';
import { RemoveToast, ToastType } from '@wix/business-manager-api';
import { BusinessManagerService } from '@src/sevices/business-manager/business-manager';
import { sendBIReviewRequirementsAlert } from '@src/context/actions/bi';

interface Props
  extends InjectedTranslateProps,
    Pick<Actions, 'validatePrivacyPolicy'>,
    Pick<BusinessManagerService, 'showToast'> {
  appEnabled: boolean;
  hasPendingChanges: boolean;
  reviewRequirementApproved: boolean;
  saveFlowStatus: SaveFlowStatus;
  liveSiteUrl: string;
  kbLinks: KBLinks;
  enableAppAndSaveChanges(): Promise<void>;
  setSaveStatus(status: SaveFlowStatus): void;
}

export class SaveAndPublishComponent extends React.PureComponent<Props> {
  removeToast?: RemoveToast;

  onSaveClicked = async () => {
    const { t, validatePrivacyPolicy, enableAppAndSaveChanges, appEnabled, setSaveStatus, showToast } = this.props;
    const validations = await validatePrivacyPolicy();

    if (appEnabled) {
      if (validations.reviewRequirements === 'invalid') {
        this.removeToast && this.removeToast();

        const result = showToast({
          biName: 'consent-policy',
          type: ToastType.ERROR,
          message: t('review.cookie.policy.error.toast'),
        });
        this.removeToast = result?.remove;
        sendBIReviewRequirementsAlert();
        return setSaveStatus(SaveFlowStatus.ReviewRequirementsMissing);
      } else if (validations.privacyPolicyLink === 'invalid') {
        return setSaveStatus(SaveFlowStatus.ApproveMissingLink);
      }
    }

    await enableAppAndSaveChanges();
  };

  onCommitChanges = () => this.props.enableAppAndSaveChanges();
  onModalCancel = () => this.props.setSaveStatus(SaveFlowStatus.None);
  onGoToLiveSite = () => {
    window.open(this.props.liveSiteUrl, '_blank');
    this.onModalCancel();
  };

  render() {
    const { t, hasPendingChanges, saveFlowStatus, kbLinks } = this.props;
    const showMissingLinkMessage = saveFlowStatus === SaveFlowStatus.ApproveMissingLink;
    const showSuccessMessage = saveFlowStatus === SaveFlowStatus.SuccessfullySaved;
    const showFailedMessage = saveFlowStatus === SaveFlowStatus.FailedToSave;
    return (
      <>
        <Button dataHook={DataHooks.SaveAndPublishButton} disabled={!hasPendingChanges} onClick={this.onSaveClicked}>
          {t('save.and.publish')}
        </Button>
        <MissingLinkPrompt
          learnMoreKBLink={kbLinks.CreatingAPrivacyPolicy}
          isOpen={showMissingLinkMessage}
          onOk={this.onCommitChanges}
          onCancel={this.onModalCancel}
        />
        <SuccessPrompt isOpen={showSuccessMessage} onOk={this.onGoToLiveSite} onCancel={this.onModalCancel} />
        <FailedPrompt
          supportKBLink={kbLinks.ContactSupport}
          isOpen={showFailedMessage}
          onOk={this.onCommitChanges}
          onCancel={this.onModalCancel}
        />
      </>
    );
  }
}
